import {Term} from '@benchling/units';
import React, {useState} from 'react';

import {BaseUnitType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  unitTypeFormulaMatchesConcentration,
  unitTypeFormulaMatchesContainerQuantity,
} from 'inventory/InventoryUnitUtil';
import Modal from 'ui-library/modals/Modal';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import setIntersection from 'util/setIntersection';

import {UpdateUnitTypeParams} from './UnitDictionaryDAO';
import UnitTypeForm, {useFormulaState, formatFormula} from './UnitTypeForm';
import {HydratedUnit, hydrateGqlUnit} from './util';

type UnitType = UnitTypesQuery['unitTypes'][number];

export interface UnitTypeEditModalProps {
  unitType: UnitType;
  allUnitTypes: UnitTypesQuery['unitTypes'];
  onRequestClose: () => void;
  onRequestSubmit: (result: Partial<UpdateUnitTypeParams>) => void;
}

export function isBase(unitType: UnitType): boolean {
  return unitType.terms.length === 1 && unitType.terms[0].exponent === 1;
}

export function getInitialFormulaUnits(
  unitType: UnitType,
  allUnitTypes: UnitTypesQuery['unitTypes']
): {numerator: Array<HydratedUnit>; denominator: Array<HydratedUnit>} {
  const numerator: Array<HydratedUnit> = [];
  const denominator: Array<HydratedUnit> = [];

  if (!unitType.formula) {
    return {numerator, denominator};
  }

  const unitById: {[id: string]: {unit: UnitType['units'][number]; parentUnitType: UnitType}} = {};
  for (const ut of allUnitTypes) {
    for (const u of ut.units) {
      unitById[u.id] = {unit: u, parentUnitType: ut};
    }
  }

  for (const term of unitType.formula) {
    const found = unitById[term.value.id];
    const hydrated = hydrateGqlUnit(found.unit, found.parentUnitType);
    const count = Math.abs(term.exponent);
    const target = term.exponent > 0 ? numerator : denominator;
    for (let i = 0; i < count; i++) {
      target.push(hydrated);
    }
  }

  return {numerator, denominator};
}

export function getInventoryEligibilityWarning(
  unitType: UnitType,
  newTerms: Array<Term<BaseUnitType>>
): string | undefined {
  const newUnitType = {
    terms: newTerms,
  };

  if (unitType.overrideIsConcentration || unitType.overrideIsContainerQuantity) {
    return undefined;
  }

  if (
    unitTypeFormulaMatchesContainerQuantity(unitType) &&
    !unitTypeFormulaMatchesContainerQuantity(newUnitType)
  ) {
    return 'This unit type will no longer be available by default as a container quantity in the Inventory product. You can still override this later under Inventory-specific settings.';
  }

  if (unitTypeFormulaMatchesConcentration(unitType) && !unitTypeFormulaMatchesConcentration(newUnitType)) {
    return 'This unit type will no longer be available by default as a concentration in the Inventory product. You can still override this later under Inventory-specific settings.';
  }

  return undefined;
}

export function getDependencyWarning(
  unitType: UnitType,
  allUnitTypes: UnitTypesQuery['unitTypes']
): string | undefined {
  const unitIds = new Set(unitType.units.map((u) => u.id));
  const dependentUnitTypes = allUnitTypes.filter((ut) => {
    if (!ut.formula) {
      return false;
    }
    const formulaUnitIds = new Set(ut.formula.map((term) => term.value.id));
    return setIntersection(formulaUnitIds, unitIds).size > 0;
  });

  if (dependentUnitTypes.length > 0) {
    return `The following compound Unit Types refer to this Unit Type and their interpretation may be affected by formula changes: ${dependentUnitTypes.map((ut) => ut.name).join(', ')}`;
  }

  return undefined;
}

export default function UnitTypeEditModal({
  unitType,
  allUnitTypes,
  onRequestClose,
  onRequestSubmit,
}: UnitTypeEditModalProps): JSX.Element {
  const initial = getInitialFormulaUnits(unitType, allUnitTypes);
  const [name, setName] = useState(unitType.name);

  const {
    numeratorUnits,
    setNumeratorUnits,
    denominatorUnits,
    setDenominatorUnits,
    combinedTerms,
    simplifiedTerms,
  } = useFormulaState(initial.numerator, initial.denominator);

  const classificationWarning = getInventoryEligibilityWarning(unitType, simplifiedTerms);
  const dependencyWarning = getDependencyWarning(unitType, allUnitTypes);
  const warningMessages: Array<string> = [
    ...(classificationWarning ? [classificationWarning] : []),
    ...(dependencyWarning ? [dependencyWarning] : []),
  ];

  return (
    <Modal
      title="Edit Unit Type"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({
          name: name.trim(),
          terms: simplifiedTerms,
          formula: formatFormula(combinedTerms),
        });
      }}
      large={true}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Save',
          submit: true,
          disabled: !name.trim(),
        },
      ]}
      dataTestAttrs={{component: 'UnitTypeEditModal'}}
    >
      <UnitTypeForm
        name={name}
        onNameChange={setName}
        simplifiedTerms={simplifiedTerms}
        numeratorUnits={numeratorUnits}
        onSetNumeratorUnits={setNumeratorUnits}
        denominatorUnits={denominatorUnits}
        onSetDenominatorUnits={setDenominatorUnits}
        warningMessages={warningMessages}
        errorMessage=""
      />
    </Modal>
  );
}
